// Shared behaviour for native <dialog> drawers and popups ('@purolabs/dialog'): page scroll lock while open, close on
// a backdrop click, Escape left alone while an input method is composing, and focus back on the opener after closing.
import { lockScroll, unlockScroll, isClickedOutside } from '@theme/utilities';

const openers = new WeakMap();

// How the shopper last interacted. A modal moves focus to its first control (the close button), and Safari shows the
// keyboard focus ring there even after a tap, boxing the X; only keyboard users need focus on that button.
let usingKeyboard = false;
document.addEventListener('keydown', () => (usingKeyboard = true), true);
document.addEventListener('pointerdown', () => (usingKeyboard = false), true);

/** Opens a dialog as a modal and remembers what opened it. */
export function openDialog(dialog, opener = document.activeElement) {
  if (!dialog || dialog.open) return;
  openers.set(dialog, opener);
  dialog.showModal();
  if (!usingKeyboard) {
    if (!dialog.hasAttribute('tabindex')) dialog.tabIndex = -1;
    dialog.focus({ preventScroll: true });
  }
  lockScroll(dialog);
}

export function closeDialog(dialog) {
  if (dialog?.open) dialog.close();
}

/** Wires one dialog's backdrop, Escape and close handling; pass the owner's AbortSignal so it is removed with it. */
export function setupDialog(dialog, signal) {
  if (!dialog) return;
  // Both the press and the release must land on the backdrop, so a text selection dragged out of the panel doesn't close it
  let pressedOutside = false;
  dialog.addEventListener('pointerdown', (event) => (pressedOutside = isClickedOutside(event, dialog)), { signal });
  dialog.addEventListener('click', (event) => {
    if (pressedOutside && isClickedOutside(event, dialog)) dialog.close();
    pressedOutside = false;
  }, { signal });
  dialog.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && event.isComposing) event.preventDefault();
  }, { signal });
  dialog.addEventListener('close', () => {
    unlockScroll(dialog);
    const opener = openers.get(dialog);
    openers.delete(dialog);
    const focusLost = !document.activeElement || document.activeElement === document.body || dialog.contains(document.activeElement);
    if (opener?.isConnected && focusLost) opener.focus({ preventScroll: true });
  }, { signal });
}
